import React, { useEffect, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Button, Input, Popover, Space, Tooltip } from 'antd';
import {
  BoldOutlined,
  CodeOutlined,
  ItalicOutlined,
  LinkOutlined,
  OrderedListOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';
import './RichTextEditor.css';

interface RichTextEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
}) => {
  const lastExternalValue = useRef(value);
  const isUpdatingFromProps = useRef(false);
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [sourceMode, setSourceMode] = useState(false);

  const editor = useEditor({
    extensions: [StarterKit, Link.configure({ openOnClick: false, autolink: false })],
    content: value || '',
    editable: !disabled,
    editorProps: {
      attributes: {
        class: 'rte-prosemirror',
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
    onUpdate: ({ editor }) => {
      if (isUpdatingFromProps.current) return;
      const html = editor.getHTML();
      lastExternalValue.current = html;
      onChange?.(html);
    },
  });

  // Sync external value changes (e.g. form reset or initial data load)
  useEffect(() => {
    if (editor && value !== lastExternalValue.current) {
      isUpdatingFromProps.current = true;
      lastExternalValue.current = value;
      editor.commands.setContent(value || '');
      isUpdatingFromProps.current = false;
    }
  }, [editor, value]);

  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled);
    }
  }, [editor, disabled]);

  const btn = (active: boolean) => (active ? 'primary' : 'text') as 'primary' | 'text';

  const openLinkPopover = () => {
    setLinkUrl(editor?.getAttributes('link').href || '');
    setLinkPopoverOpen(true);
  };

  const applyLink = () => {
    const url = linkUrl.trim();
    if (url) {
      editor?.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    } else {
      editor?.chain().focus().extendMarkRange('link').unsetLink().run();
    }
    setLinkPopoverOpen(false);
  };

  return (
    <div className={`rte-wrap${disabled ? ' rte-wrap--disabled' : ''}`}>
      {!disabled && (
        <div className="rte-toolbar">
          <Space size={2}>
            {!sourceMode && (
              <>
                <Tooltip title="Negrito">
                  <Button
                    type={btn(!!editor?.isActive('bold'))}
                    size="small"
                    icon={<BoldOutlined />}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().toggleBold().run();
                    }}
                  />
                </Tooltip>
                <Tooltip title="Itálico">
                  <Button
                    type={btn(!!editor?.isActive('italic'))}
                    size="small"
                    icon={<ItalicOutlined />}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().toggleItalic().run();
                    }}
                  />
                </Tooltip>
                <div className="rte-toolbar-sep" />
                <Tooltip title="Lista com marcadores">
                  <Button
                    type={btn(!!editor?.isActive('bulletList'))}
                    size="small"
                    icon={<UnorderedListOutlined />}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().toggleBulletList().run();
                    }}
                  />
                </Tooltip>
                <Tooltip title="Lista numerada">
                  <Button
                    type={btn(!!editor?.isActive('orderedList'))}
                    size="small"
                    icon={<OrderedListOutlined />}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      editor?.chain().focus().toggleOrderedList().run();
                    }}
                  />
                </Tooltip>
                <div className="rte-toolbar-sep" />
                <Popover
                  open={linkPopoverOpen}
                  onOpenChange={(open) => {
                    if (!open) setLinkPopoverOpen(false);
                  }}
                  trigger="click"
                  placement="bottomLeft"
                  content={
                    <Space.Compact>
                      <Input
                        size="small"
                        placeholder="https://exemplo.com"
                        value={linkUrl}
                        autoFocus
                        onChange={(e) => setLinkUrl(e.target.value)}
                        onPressEnter={applyLink}
                        style={{ width: 220 }}
                      />
                      <Button size="small" type="primary" onClick={applyLink}>
                        Aplicar
                      </Button>
                    </Space.Compact>
                  }
                >
                  <Tooltip title="Link">
                    <Button
                      type={btn(!!editor?.isActive('link'))}
                      size="small"
                      icon={<LinkOutlined />}
                      aria-label="Inserir link"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        openLinkPopover();
                      }}
                    />
                  </Tooltip>
                </Popover>
                <div className="rte-toolbar-sep" />
              </>
            )}
            <Tooltip title={sourceMode ? 'Voltar para o editor visual' : 'Editar HTML diretamente'}>
              <Button
                type={btn(sourceMode)}
                size="small"
                icon={<CodeOutlined />}
                aria-label="Alternar edição de HTML"
                onClick={() => setSourceMode((prev) => !prev)}
              />
            </Tooltip>
          </Space>
        </div>
      )}
      {sourceMode ? (
        <Input.TextArea
          className="rte-source"
          value={value}
          onChange={(e) => onChange?.(e.target.value)}
          placeholder={placeholder}
          autoSize={{ minRows: 4 }}
          disabled={disabled}
        />
      ) : (
        <EditorContent editor={editor} />
      )}
    </div>
  );
};
